import React, { memo, useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _set from 'lodash/set';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import {
    Col,
    Row,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { filterMobileToolbarClick, myWalletMobileToolbarClick } from 'reaxl-analytics-handlers';
import { FilteredResultsControls } from 'reaxl-filters';

import { srpSortDropDownClick } from '@/analytics/srpAnalyticsHandlers';

import inventorySortOptions from '@/config/sort';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import MobileSearchFilterButtons from '@/components/SearchLink/MobileSearchFilterButtons';

import InventoryClearFilters from './InventoryClearFilters';
import SaveSearchContainer from './SaveSearchContainer';
import SuggestedFilterContainer from './SuggestedFilterContainer';

function FilterMobileControlsContainer({
    renderCompareToggle,
    isStickyHeader,
}) {
    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLg = _get(device, 'is.lg', false);
    const lessThanMd = _get(device, 'lessThan.md', false);

    const {
        compare: [showCompare],
        my_wallet: [enableMyWallet],
        remove_payments_toggle: [removePaymentsToggle, {
            wallet_text: optimizelyWalletText,
        }],
        srp_redesigned_sticky_header: [redesignedStickyHeader],
    } = useFeatures([
        'compare',
        'my_wallet',
        'remove_payments_toggle',
        'srp_redesigned_sticky_header',
    ]);

    const currentSort = useSelector(srpSortDuck.selectors.getDuckState, _isEqual);

    // do not update selected filter values until the page is done loading
    const filters = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);
    const filterCount = filters.length;

    const sortData = useMemo(
        () => ({ ...inventorySortOptions, value: currentSort }),
        [currentSort]
    );

    const handleSortChange = useCallback(async (event) => {
        const sortType = _get(event, 'target.value', null) || event;
        await interactionResponse();

        _set(event, 'target.value', sortType);

        // set sort based on target value
        dispatch(srpSortDuck.creators.setSort(sortType));

        sendClick(srpSortDropDownClick, event, {
            sortType,
        });
        navigateToSrp({ resetPagination: true, isNewSearch: false });
    }, [dispatch, navigateToSrp]);

    const handleWalletClick = useCallback(async (event) => {
        await interactionResponse();
        dispatch(paymentsDuck.creators.updateMyWallet({ displayModal: true }));
        sendClick(myWalletMobileToolbarClick, event);
    }, [dispatch]);

    const handleFilterClick = useCallback(async (event) => {
        await interactionResponse();
        dispatch(srpFiltersDuck.creators.showFilters());
        sendClick(filterMobileToolbarClick, event);
    }, [dispatch]);

    if (redesignedStickyHeader) {
        return (
            <Row>
                <Col
                    xs={12}
                    className="display-flex justify-items-center align-items-center flex-direction-row w-100"
                >
                    {!isLg && <MobileSearchFilterButtons />}
                    <div
                        className="display-flex flex-nowrap"
                        style={{ overflow: 'auto' }}
                    >
                        {isLg
                            && (
                                <>
                                    <SaveSearchContainer
                                        key="saveSearchContainer"
                                    />
                                    <InventoryClearFilters
                                        key="clearFilters"
                                        className="margin-right-3"
                                    />
                                </>
                            )}
                        <div
                            style={{ minWidth: '100px' }}
                        >
                            <SuggestedFilterContainer />
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    return (
        <FilteredResultsControls
            sortData={sortData}
            onFilterClick={handleFilterClick}
            onSortByChange={handleSortChange}
            onWalletClick={handleWalletClick}
            renderCompareToggle={renderCompareToggle}
            filterCount={filterCount}
            showCompare={showCompare}
            showWallet={(enableMyWallet || isXs)}
            showFilter={(showCompare && isXs) || lessThanMd}
            paymentsDuck={paymentsDuck}
            optimizelyWalletText={removePaymentsToggle ? optimizelyWalletText : undefined}
            isStickyHeader={isStickyHeader}
        />
    );
}

export default memo(FilterMobileControlsContainer);
